/*    
<summary>
  This functional component "AddManagTreatmentementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import Popover from "@mui/material/Popover";
import { RichTreeView } from "@mui/x-tree-view";
import dayjs from "dayjs";
import {
  ErrorMessage,
  Field,
  FieldArray,
  Form,
  Formik,
  useFormikContext,
  type FieldProps,
} from "formik";
import { parseInt } from "lodash";
import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { DATE_FORMAT, integerRegex } from "../../../../constants/constants";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import { useStore } from "../../../../contexts/store-provider";
import {
  getUserTypeFromLocalStorage
} from "../../../../helpers/localstorage-helper";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import type { IOption } from "../../../../models/ICommon";
import AutoCalculateTax from "../../../../shared-components/auto-tax-calculation/useAutoCalculateTax";
import LoaderButton from "../../../../shared-components/button/loader-button";
import FormikDateFieldMUI from "../../../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import FormikFormCheckBox from "../../../../shared-components/FormikFormHandlers/FormikFormCheckBox";
import FormikFormImagePicker from "../../../../shared-components/FormikFormHandlers/FormikFormImagePicker";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputRegexCheckFormattedNumber from "../../../../shared-components/FormikFormHandlers/FormikFormInputRegexCheckFormattedNumber";
import FormikFormOnChangeSelect from "../../../../shared-components/FormikFormHandlers/FormikFormOnChangeSelect";
import FormikFormSelect from "../../../../shared-components/FormikFormHandlers/FormikFormSelect";
import FormikFormTextArea from "../../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import {
  AddStoreTreatmentValidationSchema,
  EditStoreTreatmentValidationSchema,
} from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import Label from "../../../../shared-components/label/label";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import { formatMessage } from "../../../../translations/format-message";

interface IProps {
  storeId: number;
  id: number;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  getTreatmentManagementLoading: boolean;
  initialValues: any;
  businessTypeOptions: IOption[];
  categoryOptions: IOption[];
  timeOptions: IOption[];
  taxOptions: IOption[];
  registrationTypeOptions: IOption[];
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
  handleDeleteSubmitHandler: () => void;
  deleteActionLoading: boolean;
  getAllStaffOptions: IOption[];
}

const StoreAddTreatmentManagementForm = (props: IProps) => {
  const {
    showDeletePopup,
    deletePopupToggleHandler,
    handleDeleteSubmitHandler,
    deleteActionLoading,
  } = props;
  const { categoryManagementStore } = useStore();
  const { GetCategoryListByStoreIdService } = categoryManagementStore;
  const [selectedItems, setSelectedItems] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [itemDetail, setItemDetail] = useState(
    formatMessage("choose_category")
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBusinessType, setselectedBusinessType] = useState(-1);

  const isValidDate = (date: any) => {
    return dayjs(date).isValid();
  };

  // useEffect(() => {
  //   if (props.id > 0) {
  //     let path = findObjByKeyWithPath(
  //       props.categoryOptions,
  //       props.initialValues?.CategoryId
  //         ? Number(props.initialValues?.CategoryId)
  //         : props.initialValues?.CategoryId
  //     )?.path;
  //     let obj = findObjByKeyWithPath(
  //       props.categoryOptions,
  //       props.initialValues?.CategoryId
  //         ? Number(props.initialValues?.CategoryId)
  //         : props.initialValues?.CategoryId
  //     )?.obj;
  //     setSelectedCategory(obj);
  //     setSelectedItems(obj?.id);
  //     setItemDetail(path ? path : formatMessage("choose_category"));
  //   }
  // }, [props.categoryOptions, props.id, props.initialValues]);

  const formik = useFormikContext();

  // useEffect(() => {
  //   const businessTypeId =
  //     selectedBusinessType > -1
  //       ? selectedBusinessType
  //       : props.initialValues?.BusinessTypeId;

  //   setSelectedItems("");
  //   setSelectedCategory(null);
  //   setItemDetail(formatMessage("choose_category"));

  //   if (formik) {
  //     formik.setFieldValue("CategoryPath", "");
  //     formik.setFieldTouched("CategoryPath", true);
  //   }
  // }, [selectedBusinessType, props.initialValues, formik]);

  // Update the useEffect for initial category selection
  useEffect(() => {
    if (
      props.id > 0 &&
      props.initialValues?.CategoryId &&
      props.categoryOptions?.length > 0
    ) {
      // Ensure CategoryId is a number
      const categoryId = Number(props.initialValues.CategoryId);

      // Find the category in the tree
      const foundCategory = findCategoryInTree(
        props.categoryOptions,
        categoryId
      );

      if (foundCategory) {
        setSelectedCategory(foundCategory.obj);
        setSelectedItems(String(foundCategory.obj.key)); // Ensure this is a string if RichTreeView expects strings
        setItemDetail(foundCategory.path || formatMessage("choose_category"));

        // Set the form field value
        if (formik) {
          formik.setFieldValue("CategoryPath", foundCategory.path);
        }
      }
    }
  }, [props.categoryOptions, props.id, props.initialValues]);

  // Helper function to find category in tree
  const findCategoryInTree = (
    categories: any[],
    targetId: number
  ): { obj: any; path: string } | null => {
    for (const category of categories) {
      if (category.key === targetId) {
        return {
          obj: category,
          path: category.title,
        };
      }

      if (category.children && category.children.length > 0) {
        const foundInChildren = findCategoryInTree(category.children, targetId);
        if (foundInChildren) {
          return {
            obj: foundInChildren.obj,
            path: `${category.title} > ${foundInChildren.path}`,
          };
        }
      }
    }
    return null;
  };

  const handleDelete = () => {
    deletePopupToggleHandler();
  };

  const handleDeleteModalClosed = () => {
    deletePopupToggleHandler();
  };

  const findObjByKeyWithPath = (
    data: any[],
    key: number
  ): { obj: any; path: string } | null => {
    const search = (
      data: any[],
      key: number,
      path: string[]
    ): { obj: any; path: string } | null => {
      for (const obj of data) {
        const currentPath = [...path, obj.title];

        if (obj.key === key) {
          return { obj, path: currentPath.join(" > ") };
        }

        if (obj.children && obj.children.length > 0) {
          const found = search(obj.children, key, currentPath);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    // Start the search with an empty path
    return search(data, key, []);
  };

  const isOpen = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const formatDate = (date: string | Date) => dayjs(date).format(DATE_FORMAT);

  return (
    <>
      {showDeletePopup && (
        <DeletePopup
          modalSubmit={handleDeleteSubmitHandler}
          modalClosed={handleDeleteModalClosed}
          isLoading={deleteActionLoading}
          title={formatMessage("delete_confirmation")}
          message={formatMessage(
            "are_you_sure_you_want_to_delete_this_treatment"
          )}
        />
      )}
      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="treatment_registration" />
            ) : (
              <FormattedMessage id="treatment_details" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.getTreatmentManagementLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          enableReinitialize
          validationSchema={
            props.id === -1
              ? AddStoreTreatmentValidationSchema(selectedCategory)
              : EditStoreTreatmentValidationSchema(selectedCategory)
          }
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values) => {
            let obj = {
              ...values,
              Image: values.Image,
              RegistrationType: values.RegistrationType,
              BusinessTypeId: values.BusinessTypeId,
              CategoryId:
                selectedCategory !== null ? Number(selectedItems) : -1,
              TreatmentName: values.TreatmentName,
              FeeIncludingTax: toHalfWidth(values.FeeIncludingTax),
              Status: values.Status,
              StartDate: isValidDate(values.StartDate)
                ? formatDate(values.StartDate)
                : null,
              EndDate: isValidDate(values.EndDate)
                ? formatDate(values.EndDate)
                : null,
              Note: values?.Note,
              IsReservationSite: values.IsReservationSite,
              Time: values.Time,
              TimeValue:
                props.timeOptions.find(
                  (item: IOption) => item.id == values.Time
                )?.value ?? 0,
              OptionMenu: Number(values.OptionMenu),
              TaxId: values.TaxId,
              TaxAmount: values.TaxAmount || 0,
              TaxValue:
                props.taxOptions.find(
                  (item: IOption) => item.id == values.TaxId
                )?.value ?? 0,
              FeeExcludingTax: values.FeeExcludingTax || 0,
              Description: values.Description,
              IsFeatured: values.IsFeatured,
              StaffOptions: values.StaffOptions.filter(
                (item: any) => item !== -1
              ),
              IsCategoryChanged: false
            };
            if(obj.CategoryId !== props.initialValues?.CategoryId)
              obj.IsCategoryChanged = true;
            props.submitHandler(obj);
          }}
        >
          {({
            setFieldValue,
            setFieldTouched,
            values,
          }) => {
             const isHeadquarterTreatment = values.RegistrationType === 0 && getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store]
            const handleClick = async (
              event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
              fieldName: string
            ) => {
              const field = fieldName;

              if (field === "FeeExcludingTaxIncreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) + 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) - 1); // Uncheck the other checkbox
              } else if (field === "FeeExcludingTaxDecreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) - 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) + 1); // Uncheck the other checkbox
              }
              else if (field === "TaxAmountIncreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) - 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) + 1); // Uncheck the other checkbox
              } else if (field === "TaxAmountDecreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) + 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) - 1); // Uncheck the other checkbox
              }
            };
            return (
              <Form className="user-form">
                <AutoCalculateTax
                  id={props.id}
                  taxOptions={props.taxOptions}
                  feeExcludingTaxField="FeeExcludingTax"
                  taxIdField="TaxId"
                  taxAmountField="TaxAmount"
                  feeIncludingTaxField="FeeIncludingTax"
                />
                {props.getTreatmentManagementLoading && (
                  <FormLoader loading={props.getTreatmentManagementLoading} />
                )}
                <Modal.Body>
                  <div className="form-group mb-3 row">
                    <div className={`${getUserTypeFromLocalStorage() !==
                      userTypeEnum[userTypeEnum.Store] ? "col-sm-6" : "col-sm-12" } `}>
                      <Label
                        className="label-style"
                        required={false}
                        label="registration_type"
                      />

                      {props.id > 0 ? (
                        <Field
                          name="RegistrationTypeName"
                          as={FormikFormInput}
                          disabled={true}
                          className="form-control"
                          type="text"
                          validcharacter={["+", "(", ")", "-", "_", "&"]}
                        />
                      ) : (
                        <Field
                          name="RegistrationType"
                          as={FormikFormSelect}
                          options={props.registrationTypeOptions}
                          disabled={
                            getUserTypeFromLocalStorage() === userTypeEnum[5] ||
                            userTypeEnum[3]
                          }
                        />
                      )}
                    </div>

                    {getUserTypeFromLocalStorage() !==
                      userTypeEnum[userTypeEnum.Store] && (
                      <div className="col-sm-6">
                        <Label
                          className="label-style"
                          required={true}
                          label="business_type"
                        />
                        <Field
                          name="BusinessTypeId"
                          as={FormikFormOnChangeSelect}
                          setState={setselectedBusinessType}
                          options={props.businessTypeOptions}
                          disabled={
                            getUserTypeFromLocalStorage() ===
                            userTypeEnum[userTypeEnum.Store]
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Row 2: Category, Reservation Site */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="category"
                      />
                      {/* <Field
                        name="CategoryId"
                        className="form-control"
                        as={FormikFormSelect}
                        options={props.categoryOptions}
                      /> */}
                      <Field
                        type="text"
                        className="form-control blinkCursor form-select"
                        name="CategoryPath"
                        onClick={handleOpen}
                        value={itemDetail}
                        as={FormikFormInput}
                        onBlur={() => {
                          if (!values.CategoryPath) {
                            setFieldTouched("CategoryPath", true);
                          }
                        }}
                        disabled={isHeadquarterTreatment}
                      />

                      <Popover
                        open={isOpen}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        disableAutoFocus
                      >
                        {props.categoryOptions?.length > 0 ? (
                          <RichTreeView
                            checkboxSelection
                            selectedItems={selectedItems}
                            onSelectedItemsChange={(
                              e: any,
                              newSelectedItems: any
                            ) => {
                              if (newSelectedItems) {
                                const selectedCategoryObj =
                                  findObjByKeyWithPath(
                                    props.categoryOptions,
                                    Number(newSelectedItems)
                                  )?.obj;

                                const categoryPath = findObjByKeyWithPath(
                                  props.categoryOptions,
                                  Number(newSelectedItems)
                                )?.path!;

                                setSelectedItems(newSelectedItems);
                                setSelectedCategory(selectedCategoryObj);
                                setItemDetail(categoryPath);

                                setFieldValue("CategoryPath", categoryPath);
                                setFieldTouched("CategoryPath", false, false);
                              } else {
                                setSelectedItems("");
                                setSelectedCategory(null);
                                setItemDetail(formatMessage("choose_category"));
                                setFieldValue("CategoryPath", "");
                                setFieldTouched("CategoryPath", true);
                              }
                              handleClose();
                            }}
                            items={props.categoryOptions}
                          />
                        ) : (
                          formatMessage("no_category_added")
                        )}
                      </Popover>
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="reservation_site"
                      />
                      <Field name="IsReservationSite">
                        {({ field, form }: FieldProps) => (
                          <div className="row">
                            <div className="col-6">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="use"
                                name={field.name}
                                checked={field.value === true}
                                onChange={() =>
                                  form.setFieldValue(field.name, true)
                                }
                              />
                              <label className="form-check-label" htmlFor="use">
                                <FormattedMessage id="use" />
                              </label>
                            </div>
                            <div className="col-6">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="not_use"
                                name={field.name}
                                checked={field.value === false}
                                onChange={() =>
                                  form.setFieldValue(field.name, false)
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="not_use"
                              >
                                <FormattedMessage id="not_use" />
                              </label>
                            </div>
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>

                  {/* Row 3: Treatment Name, Time */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="treatment_name"
                      />
                      <Field
                        name="TreatmentName"
                        className="form-control"
                        type="text"
                        placeholder="enter_treatment_name"
                        // validcharacter={["+", "(", ")", "-", "_", "&"]}
                        // validcharacter={[
                        //   "+",
                        //   "-",
                        //   "[",
                        //   "]",
                        //   "|",
                        //   "*",
                        //   "&",
                        //   "/",
                        // ]}
                        validcharacter={[
                          "・",
                          "／",
                          "－",
                          "～",
                          "＋",
                          "（",
                          "）",
                          "［",
                          "］",
                          "※",
                          "：",
                          "★",
                          "☆",
                          "◎",
                          "○",
                          "●",
                          "◆",
                          "◇",
                          "■",
                          "□",
                          "→",
                          "⇒",
                          "♪",
                          "(",
                          ")",
                          "【",
                          "】",
                          "￥",
                          '"',
                          "＜",
                          "＞",
                          "！",
                          "？",
                          "↓",
                          "↑",
                          "←",
                          "．",
                          '"',
                          "゛",
                          "'",
                          "'",
                          '"',
                          '"',
                          "、",
                          "。",
                          "《",
                          "》",
                          "+",
                          "-",
                          "[",
                          "]",
                          "|",
                          "*",
                          "&",
                          "/",
                        ]}
                        as={FormikFormInput}
                        disabled={isHeadquarterTreatment}
                      />
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="time"
                      />
                      <Field
                        name="Time"
                        className="form-control"
                        as={FormikFormSelect}
                        options={props.timeOptions}
                      />
                    </div>
                  </div>

                  {/* Row 4: Fee, Tax Rate */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="fee(including_tax)"
                      />
                      <Field
                        name="FeeIncludingTax"
                        className="form-control"
                        type="text"
                        placeholder="enter_fee"
                        regex={integerRegex}
                        as={FormikFormInputRegexCheckFormattedNumber}
                        disabled={isHeadquarterTreatment}
                      />
                    </div>

                    {/* Tax Dropdown */}
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="tax"
                      />
                      <Field
                        name="TaxId"
                        as={FormikFormSelect}
                        options={props.taxOptions}
                        disabled={isHeadquarterTreatment}
                      />
                    </div>
                  </div>

                  {/* Auto-Filled Fields */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label className="label-style" label="tax_amount" />
                      <div className="input-group">
                        <Field
                          name="TaxAmount"
                          className="form-control"
                          type="text"
                          disabled={true}
                          regex={integerRegex}
                          as={FormikFormInputRegexCheckFormattedNumber}
                        />
                        <button
                          name="TaxAmountDecreaseBtn"
                          type="button"
                          onClick={(e) =>
                            handleClick(e, "TaxAmountDecreaseBtn")
                          }
                          className="btn btn-dark"
                          disabled={isHeadquarterTreatment ? isHeadquarterTreatment : (Math.round(parseFloat(values.FeeIncludingTax) - (parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100)))) - 1 === values.TaxAmount) || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                        >
                          <i className="bi bi-dash-lg"></i>
                        </button>
                        <button
                          name="TaxAmountIncreaseBtn"
                          type="button"
                          onClick={(e) =>
                            handleClick(e, "TaxAmountIncreaseBtn")
                          }
                          className="btn btn-dark"
                          disabled={isHeadquarterTreatment ? isHeadquarterTreatment : (Math.round(parseFloat(values.FeeIncludingTax) - parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100))) + 1 === values.TaxAmount) || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                        >
                          <i className="bi bi-plus-lg"></i>
                        </button>
                      
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        label="fee(excluding_tax)"
                      />
                      <div className="input-group">
                        <Field
                          name="FeeExcludingTax"
                          className="form-control"
                          type="text"
                          disabled={true}
                          regex={integerRegex}
                          as={FormikFormInputRegexCheckFormattedNumber}
                        />
                        
                        <button
                          name="FeeExcludingTaxDecreaseBtn"
                          className="btn btn-dark"
                          type="button"
                          onClick={(e) =>
                            handleClick(e, "FeeExcludingTaxDecreaseBtn")
                          }
                          disabled={isHeadquarterTreatment ? isHeadquarterTreatment : (Math.round(parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100))) - 1 === values.FeeExcludingTax) || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                        >
                          <i className="bi bi-dash-lg"></i>
                        </button>
                        <button
                          name="FeeExcludingTaxIncreaseBtn"
                          className="btn btn-dark"
                          type="button"
                          onClick={(e) =>
                            handleClick(e, "FeeExcludingTaxIncreaseBtn")
                          }
                          disabled={isHeadquarterTreatment ? isHeadquarterTreatment : (Math.round(parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100))) + 1 === values.FeeExcludingTax)  || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                        >
                          <i className="bi bi-plus-lg"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className="form-group mb-0">
                    <Label className="label-style" label="Description" />
                    <Field
                      name="Description"
                      as={FormikFormTextArea}
                      className="form-control"
                      placeholder="enter_description"
                      disabled={
                        values.RegistrationType === 0 &&
                        getUserTypeFromLocalStorage() ===
                          userTypeEnum[userTypeEnum.Store]
                      }
                      validcharacter={[
                        "・",
                        "／",
                        "－",
                        "～",
                        "＋",
                        "（",
                        "）",
                        "［",
                        "］",
                        "※",
                        "：",
                        "★",
                        "☆",
                        "◎",
                        "○",
                        "●",
                        "◆",
                        "◇",
                        "■",
                        "□",
                        "→",
                        "⇒",
                        "♪",
                        "(",
                        ")",
                        "【",
                        "】",
                        "￥",
                        '"',
                        "＜",
                        "＞",
                        "！",
                        "？",
                        "↓",
                        "↑",
                        "←",
                        "．",
                        '"',
                        "゛",
                        "'",
                        "'",
                        '"',
                        '"',
                        "、",
                        "。",
                        "《",
                        "》",
                        "+",
                        "-",
                        "[",
                        "]",
                        "|",
                        "*",
                        "&",
                        "/",
                        ".",
                        "!",
                        "@",
                        "#",
                        "$",
                        "%",
                        "^",
                        "_",
                        "`",
                        "{",
                        "}",
                        "?",
                        ";",
                        ":",
                        ",",
                        "=",
                        "~",
                        "<",
                        ">",
                      ]}
                    />
                    <p className="small text-muted text-end mt-1">{`${
                      values.Description ? values.Description.length : 0
                    }/300`}</p>
                  </div>

                  {/* Row 5: Start Date, End Date */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="start_date"
                      />
                      <Field
                        name="StartDate"
                        as={FormikDateFieldMUI}
                        // min={dayjs().format(DATE_FORMAT)}
                        disableFuture={false}
                        disabled={isHeadquarterTreatment}
                      />
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="end_date"
                      />
                      <Field
                        name="EndDate"
                        as={FormikDateFieldMUI}
                        // min={dayjs().format(DATE_FORMAT)}
                        disableFuture={false}
                        disabled={isHeadquarterTreatment}
                      />
                    </div>
                  </div>

                  {/* Row 6: Status, Counseling Notes */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="status"
                      />
                      <div className="row">
                        <div className="col-6">
                          <Field
                            className="form-check-input"
                            name="Status"
                            type="radio"
                            value="0"
                            id="valid"
                            disabled={
                              values.RegistrationType === 0 &&
                              getUserTypeFromLocalStorage() ===
                                userTypeEnum[userTypeEnum.Store]
                            }
                          />
                          <label className="form-check-label" htmlFor="valid">
                            <FormattedMessage id="valid" />
                          </label>
                        </div>
                        <div className="col-6">
                          <Field
                            className="form-check-input"
                            name="Status"
                            type="radio"
                            value="1"
                            id="invalid"
                            disabled={
                              values.RegistrationType === 0 &&
                              getUserTypeFromLocalStorage() ===
                                userTypeEnum[userTypeEnum.Store]
                            }
                          />
                          <label className="form-check-label" htmlFor="invalid">
                            <FormattedMessage id="invalid" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="option_menu"
                      />
                      <div className="row">
                        <div className="col-6">
                          <Field
                            className="form-check-input"
                            name="OptionMenu"
                            type="radio"
                            value="0"
                            id="mandatory"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="mandatory"
                          >
                            <FormattedMessage id="mandatory" />
                          </label>
                        </div>
                        <div className="col-6">
                          <Field
                            className="form-check-input"
                            name="OptionMenu"
                            type="radio"
                            value="1"
                            id="not_mandatory"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="not_mandatory"
                          >
                            <FormattedMessage id="not_mandatory" />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group mb-3 row">
                    <div className="col-sm-12">
                      <Label
                        className="label-style"
                        required={false}
                        label="remarks"
                      />
                      <Field
                        name="Note"
                        id="CounsellingNote"
                        as={FormikFormTextArea}
                        className="form-control"
                        placeholder="enter_remarks"
                        type="text"
                        disabled={isHeadquarterTreatment}
                      />
                      <p className="small text-muted text-end mt-1">{`${
                        values.Note ? values.Note.length : 0
                      }/300`}</p>
                    </div>
                    <div className="col-sm-4">
                      <Label
                        className="label-style pb-0"
                        required={false}
                        label="upload_image"
                      />
                      <div className="col-12 mb-3">
                        <Field
                          className="form-control"
                          name="Image"
                          type="text"
                          as={FormikFormImagePicker}
                          // disabled={
                          //   values.RegistrationType === 0 &&
                          //   getUserTypeFromLocalStorage() ===
                          //     userTypeEnum[userTypeEnum.Store]
                          // }
                        />
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <Label className="label-style" label="select_staff" required={true}/>
                      <div className="p-2 border rounded mb-3 h-50 overflow-scroll">
                        {props.getAllStaffOptions.length > 0 ? (
                          <>
                            <FieldArray name="StaffOptions">
                              {({ push, remove, form }) => {
                                const selectedStaff =
                                  form.values.StaffOptions || [];
                                const allStaffIds =
                                  props.getAllStaffOptions.map((staff) =>
                                    String(staff.id)
                                  );
                                const isAllSelected =
                                  allStaffIds.length > 0 &&
                                  allStaffIds.every((id) =>
                                    selectedStaff.includes(id)
                                  );

                                const toggleSelectAll = (e: {
                                  target: { checked: any };
                                }) => {
                                  if (e.target.checked) {
                                    form.setFieldValue(
                                      "StaffOptions",
                                      allStaffIds
                                    );
                                  } else {
                                    form.setFieldValue("StaffOptions", []);
                                  }
                                };
                                return (
                                  <div className="staff-checkbox-group d-flex flex-wrap gap-3">
                                    <label className="p-0">
                                      <input
                                        type="checkbox"
                                        className="me-1"
                                        checked={isAllSelected}
                                        onChange={toggleSelectAll}
                                      />
                                      <FormattedMessage id="select_all" />
                                    </label>
                                    {props.getAllStaffOptions.map((staff) => {
                                      const staffId = String(staff.id);
                                      const isChecked =
                                        selectedStaff.includes(staffId);

                                      return (
                                        <label key={staff.id} className="p-0">
                                          <input
                                            type="checkbox"
                                            name="StaffOptions"
                                            className="me-1"
                                            value={staffId}
                                            checked={isChecked}
                                            onChange={(e) => {
                                              let updatedSelection = [
                                                ...selectedStaff,
                                              ];
                                              if (e.target.checked) {
                                                if (
                                                  !updatedSelection.includes(
                                                    staffId
                                                  )
                                                ) {
                                                  updatedSelection.push(
                                                    staffId
                                                  );
                                                }
                                              } else {
                                                updatedSelection =
                                                  updatedSelection.filter(
                                                    (id) => id !== staffId
                                                  );
                                              }

                                              form.setFieldValue(
                                                "StaffOptions",
                                                updatedSelection
                                              );
                                            }}
                                          />
                                          {staff.label}
                                        </label>
                                      );
                                    })}
                                  </div>
                                );
                              }}
                            </FieldArray>
                            <ErrorMessage
                              name="StaffOptions"
                              component="span"
                              className="text-danger mb-2"
                            />
                          </>
                        ) : (
                          <p className="text-black">
                            {/* {formatMessage("no_staff_available_please_add_staff_first")}<br/> */}
                            <span data-testid="testError" className="text-danger errorMessage">
                              <FormattedMessage id="no_staff_available_please_add_staff_first" />
                            </span>
                          </p>
                          
                        )}
                      </div>

                      <label className="label-style" htmlFor="IsFeatured">
                        <Field
                          className="mx-1"
                          name="IsFeatured"
                          type="checkbox"
                          as={FormikFormCheckBox}
                          // disabled={
                          //   values.RegistrationType === 0 &&
                          //   getUserTypeFromLocalStorage() ===
                          //     userTypeEnum[userTypeEnum.Store]
                          // }
                        />
                        {formatMessage("is_featured")}
                      </label>
                    </div>
                  </div>
                </Modal.Body>

                {/* Footer Buttons */}
                <div className="modal-footer center-content">
                  <Button
                    variant="outline-danger"
                    type="button"
                    onClick={props.onModalClosed}
                    disabled={props.getTreatmentManagementLoading}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    <FormattedMessage id="close" />
                  </Button>
                  <LoaderButton
                    type="submit"
                    iconName="bi-check-lg"
                    isLoading={props.getTreatmentManagementLoading}
                    disabled={props.getAllStaffOptions.length <= 0 ? true : false}
                    text={props.id > -1 ? "button_update" : "button_register"}
                  />
                  {props.id > -1 && values.RegistrationType !== 0 && (
                    <Button
                      variant="danger"
                      type="button"
                      onClick={handleDelete}
                      disabled={props.getTreatmentManagementLoading}
                    >
                      <i className="bi bi-trash3-fill me-1"></i>
                      <FormattedMessage id="button_delete" />
                    </Button>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default DeletePopupHOC(StoreAddTreatmentManagementForm);
